
import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function FirstTimeBuyerGuidePage() {
  const [activeStep, setActiveStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [selectedProgram, setSelectedProgram] = useState('mshda');

  const toggleStepCompletion = (stepNumber: number) => {
    setCompletedSteps(prev => 
      prev.includes(stepNumber)
        ? prev.filter(num => num !== stepNumber)
        : [...prev, stepNumber]
    );
  };

  const buyingSteps = [
    {
      number: 1,
      title: 'Get Pre-Approved for a Mortgage',
      description: 'Understand your budget and show sellers you\'re a serious buyer.',
      details: [
        'Check your credit score and improve if needed',
        'Gather financial documents (pay stubs, tax returns, bank statements)',
        'Shop around with multiple lenders for the best rates',
        'Get pre-approval letter (not just pre-qualification)'
      ],
      timeframe: '1-2 weeks',
      icon: 'ri-bank-line'
    },
    {
      number: 2,
      title: 'Find a Qualified Real Estate Agent',
      description: 'Partner with an experienced agent who understands first-time buyer needs.',
      details: [
        'Look for agents with first-time buyer experience',
        'Ask for references and check reviews',
        'Ensure they understand local market conditions',
        'Verify they can guide you through loan programs'
      ],
      timeframe: '1 week',
      icon: 'ri-user-heart-line'
    },
    {
      number: 3,
      title: 'Start House Hunting',
      description: 'Search for homes within your budget and preferred neighborhoods.',
      details: [
        'Create a wish list vs. must-have list',
        'Research neighborhoods and school districts',
        'Attend open houses and schedule private showings',
        'Take notes and photos to remember each property'
      ],
      timeframe: '2-8 weeks',
      icon: 'ri-search-line'
    },
    {
      number: 4,
      title: 'Make an Offer',
      description: 'Submit a competitive offer when you find the right home.',
      details: [
        'Research comparable sales in the area',
        'Determine your maximum offer amount',
        'Include appropriate contingencies (inspection, financing)',
        'Be prepared to negotiate terms'
      ],
      timeframe: '1-3 days',
      icon: 'ri-file-text-line'
    },
    {
      number: 5,
      title: 'Get a Home Inspection',
      description: 'Hire a professional inspector to identify any potential issues.',
      details: [
        'Schedule inspection within contingency period',
        'Attend the inspection to ask questions',
        'Review the inspection report carefully',
        'Negotiate repairs or credits if needed'
      ],
      timeframe: '1 week',
      icon: 'ri-search-eye-line'
    },
    {
      number: 6,
      title: 'Finalize Your Mortgage',
      description: 'Complete the loan application process and prepare for closing.',
      details: [
        'Submit all required documentation promptly',
        'Order homeowner\'s insurance',
        'Complete the appraisal process',
        'Do final walkthrough before closing'
      ],
      timeframe: '2-4 weeks',
      icon: 'ri-file-check-line'
    },
    {
      number: 7,
      title: 'Close on Your Home',
      description: 'Sign the final paperwork and get your keys!',
      details: [
        'Review all closing documents carefully',
        'Bring certified funds for closing costs',
        'Do final walkthrough 24 hours before closing',
        'Celebrate becoming a homeowner!'
      ],
      timeframe: '1 day',
      icon: 'ri-key-line'
    }
  ];

  const loanPrograms = {
    'mshda': {
      name: 'MSHDA (Michigan State Housing)',
      downPayment: '0% - 3%',
      creditScore: '640+',
      incomeLimit: 'Varies by county',
      benefits: [
        'Down payment assistance up to $10,000',
        'Closing cost assistance up to $7,500',
        'Below-market interest rates',
        'No monthly mortgage insurance in some cases'
      ],
      description: 'Michigan\'s premier first-time homebuyer program offering significant down payment and closing cost assistance.',
      currentRate: '5.8% - 6.2%'
    },
    'fha': {
      name: 'FHA (Federal Housing Administration)',
      downPayment: '3.5%',
      creditScore: '580+',
      incomeLimit: 'No limit',
      benefits: [
        'Low down payment requirement',
        'Flexible credit requirements',
        'Assumable loans',
        'Gift funds allowed for down payment'
      ],
      description: 'Government-backed loan program with flexible qualification requirements and low down payment.',
      currentRate: '6.0% - 6.6%'
    },
    'va': {
      name: 'VA (Veterans Affairs)',
      downPayment: '0%',
      creditScore: '620+',
      incomeLimit: 'No limit',
      benefits: [
        'No down payment required',
        'No monthly mortgage insurance',
        'Competitive interest rates',
        'No prepayment penalties'
      ],
      description: 'Exclusive benefit for veterans, active military, and eligible spouses with no down payment required.',
      currentRate: '5.8% - 6.4%'
    },
    'conventional': {
      name: 'Conventional (Fannie Mae/Freddie Mac)',
      downPayment: '3% - 5%',
      creditScore: '620+',
      incomeLimit: 'No limit',
      benefits: [
        'Low down payment options',
        'PMI can be removed',
        'Higher loan limits',
        'Flexible property types'
      ],
      description: 'Traditional mortgage option with competitive rates and flexible terms for qualified borrowers.',
      currentRate: '6.2% - 6.8%'
    }
  };

  const currentProgram = loanPrograms[selectedProgram as keyof typeof loanPrograms];
  const progressPercentage = (completedSteps.length / buyingSteps.length) * 100;

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">
              First-Time Home Buyer Guide
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your complete roadmap to homeownership in Michigan. Learn about loan programs, the buying process, and get expert guidance every step of the way.
            </p>
          </div>

          {/* Progress Tracker */}
          <div className="bg-white rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black">Your Home Buying Progress</h3>
              <div className="text-yellow-600 font-bold">
                {completedSteps.length} / {buyingSteps.length} Steps Complete
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className="bg-yellow-500 h-4 rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <p className="text-gray-600 text-sm mt-2">
              Track your progress through the home buying journey.
            </p>
          </div>

          {/* Loan Programs Section */}
          <div className="bg-white rounded-lg p-8 mb-8">
            <h2 className="text-3xl font-bold text-black mb-6 text-center">First-Time Buyer Loan Programs</h2>
            
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {Object.entries(loanPrograms).map(([key, program]) => (
                <button
                  key={key}
                  onClick={() => setSelectedProgram(key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    selectedProgram === key
                      ? 'bg-yellow-500 text-black'
                      : 'bg-gray-100 text-black hover:bg-gray-200'
                  }`}
                >
                  {program.name.split(' ')[0]}
                </button>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-black mb-4">{currentProgram.name}</h3>
                <p className="text-gray-600 mb-6">{currentProgram.description}</p>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Down Payment</span>
                    <span className="font-semibold text-black">{currentProgram.downPayment}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Credit Score</span>
                    <span className="font-semibold text-black">{currentProgram.creditScore}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Income Limit</span>
                    <span className="font-semibold text-black">{currentProgram.incomeLimit}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Current Rate</span>
                    <span className="font-semibold text-black">{currentProgram.currentRate}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-bold text-black mb-4">Program Benefits</h4>
                <div className="space-y-3">
                  {currentProgram.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <i className="ri-check-line text-yellow-500 text-lg mt-0.5"></i>
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h5 className="font-semibold text-black mb-2">
                    <i className="ri-lightbulb-line text-yellow-500 mr-2"></i>
                    Pro Tip
                  </h5>
                  <p className="text-gray-700 text-sm">
                    {selectedProgram === 'mshda' && "MSHDA funds are limited and allocated on a first-come, first-served basis. Apply early in the year for the best chance of securing assistance."}
                    {selectedProgram === 'fha' && "FHA loans are assumable, which can be a valuable selling point if rates rise in the future."}
                    {selectedProgram === 'va' && "VA loans can be used multiple times and don't require mortgage insurance, making them extremely valuable for eligible borrowers."}
                    {selectedProgram === 'conventional' && "Once you reach 20% equity, you can request to remove PMI, potentially saving hundreds per month."}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step-by-Step Process */}
          <div className="bg-white rounded-lg p-8">
            <h2 className="text-3xl font-bold text-black mb-8 text-center">Your Home Buying Journey</h2>
            
            <div className="space-y-6">
              {buyingSteps.map((step) => (
                <div key={step.number} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  <div 
                    className={`p-6 cursor-pointer transition-colors ${
                      activeStep === step.number ? 'bg-yellow-500' : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => setActiveStep(activeStep === step.number ? 0 : step.number)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStepCompletion(step.number);
                          }}
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                            completedSteps.includes(step.number)
                              ? 'bg-green-500 text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {completedSteps.includes(step.number) ? (
                            <i className="ri-check-line"></i>
                          ) : (
                            step.number
                          )}
                        </button>
                        
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          activeStep === step.number ? 'bg-black text-white' : 'bg-white text-yellow-500'
                        }`}>
                          <i className={`${step.icon} text-xl`}></i>
                        </div>
                        
                        <div>
                          <h3 className={`text-lg font-bold ${
                            activeStep === step.number ? 'text-black' : 'text-black'
                          }`}>
                            {step.title}
                          </h3>
                          <p className={`text-sm ${
                            activeStep === step.number ? 'text-black' : 'text-gray-600'
                          }`}>
                            {step.description}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <div className={`text-sm font-medium ${
                          activeStep === step.number ? 'text-black' : 'text-gray-500'
                        }`}>
                          {step.timeframe}
                        </div>
                        <i className={`ri-arrow-${activeStep === step.number ? 'up' : 'down'}-s-line text-xl ${
                          activeStep === step.number ? 'text-black' : 'text-gray-400'
                        }`}></i>
                      </div>
                    </div>
                  </div>
                  
                  {activeStep === step.number && (
                    <div className="p-6 bg-white border-t border-gray-200">
                      <h4 className="font-bold text-black mb-3">What to do in this step:</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {step.details.map((detail, index) => (
                          <div key={index} className="flex items-start space-x-2">
                            <i className="ri-checkbox-circle-line text-yellow-500 mt-0.5"></i>
                            <span className="text-gray-700 text-sm">{detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <div className="bg-yellow-500 text-black rounded-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">Ready to Start Your Home Buying Journey?</h3>
              <p className="text-black mb-6">
                Let's discuss your goals and create a personalized plan to get you into your first home. I'll guide you through every step and help you take advantage of the best loan programs available.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-black text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-colors font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Schedule Buyer Consultation
                </button>
                <button className="bg-transparent border-2 border-black text-black px-8 py-3 rounded-lg hover:bg-black hover:text-white transition-colors font-semibold cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  Call: (248) 248-4801
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
